import { Button, Form, FormInstance, Select } from "antd";
import { Dispatch, Fragment, SetStateAction, useState } from "react";
import { useAppSelector } from "../../../../../app/store";
import CloseIcon from "../../../../../assets/icons/CloseIcon";
import DeleteIcon from "../../../../../assets/icons/DeleteIcon";
import DoubleCheckIcon from "../../../../../assets/icons/DoubleCheckIcon";
import EditIcon from "../../../../../assets/icons/EditIcon";
import SelectDropdownLgIcon from "../../../../../assets/icons/SelectDropdownLgIcon";
import { useGetAllAssignProjectQuery } from "../../../../../features/portal/companyOnboard/companyOnboardApi";
import {
  useGetAllProductQuery,
  useGetAllRoleQuery,
} from "../../../../../features/portal/Products/productsApi";
import useError from "../../../../../hooks/useError";
import { selectSearchOption } from "../../../../../utils/utils";
import AddIcon from "../_icons/AddIcon";

type TList = {
  productId?: number;
  productName?: string;
  groupId?: number;
  groupName?: string;
  isEdit?: boolean;
};

type Props = {
  list: TList[];
  setList: Dispatch<SetStateAction<TList[]>>;
  addForm?: FormInstance;
};

type TRole = {
  id: number;
  roleName: string;
};

export default function UserProductManage({ list, setList, addForm }: Props) {
  const [internalForm] = Form.useForm();
  const form = addForm || internalForm;
  const productId = Form.useWatch("productId", form);
  const groupId = Form.useWatch("groupId", form);

  const [editIndex, setEditIndex] = useState<number | null>(null);

  const { user, role } = useAppSelector((state) => state.auth);
  const companyId = user?.data?.company_id;

  // PRODUCT LIST
  const {
    isLoading: isProductLoading,
    isError: isProductError,
    error: productError,
    data: getProduct,
  } = useGetAllProductQuery(
    {
      params: { page: 1, limit: 10000, status: true },
    },
    { skip: role !== "SUPER_ADMIN" }
  );
  useError(isProductError, productError, "Fetch product data fail");

  // ASSIGN PROJECT LIST
  const {
    isLoading: isAssignLoading,
    isError: isAssignError,
    error: assignError,
    data: getAssign,
  } = useGetAllAssignProjectQuery(
    { id: Number(companyId) },
    { skip: !companyId }
  );
  useError(isAssignError, assignError, "Fetch product data fail");

  // GROUP PERMISSION
  const { isLoading, isError, error, data } = useGetAllRoleQuery(
    {
      params: { page: 1, limit: 10000, projectId: Number(productId) },
    },
    { skip: !productId }
  );
  useError(isError, error, "Fetch permission data fail");

  const roleList: TRole[] = Array.isArray(data?.data?.data)
    ? data?.data?.data ?? []
    : [];

  const projectList =
    role === "SUPER_ADMIN"
      ? getProduct?.data?.data?.map((el) => ({
          label: el?.name,
          value: el?.id,
        }))
      : getAssign?.data?.projects?.map((el) => ({
          label: el?.project_name,
          value: el?.project_id,
        }));

  const handleAdd = () => {
    const selectedProductId = Number(form.getFieldValue("productId") ?? productId);
    const selectedGroupId = Number(form.getFieldValue("groupId") ?? groupId);
    if (!selectedProductId || !selectedGroupId) return;

    const listItem: TList = {
      productId: selectedProductId,
      productName: projectList?.find((el) => el?.value === selectedProductId)
        ?.label,
      groupId: selectedGroupId,
      groupName: roleList?.find((el) => el?.id === selectedGroupId)?.roleName,
    };

    setList((prev) => [...prev, listItem]);
    form.resetFields(["productId", "groupId"]);
  };

  const handleEdit = (item: TList, index: number) => {
    setEditIndex(index);
    form.setFieldsValue({
      productId: item.productId,
      groupId: item.groupId,
    });
  };

  const handleUpdate = (index: number) => {
    const selectedProductId = Number(form.getFieldValue("productId"));
    const selectedGroupId = Number(form.getFieldValue("groupId"));
    const updatedList = [...list];
    updatedList[index] = {
      ...updatedList[index],
      productId: selectedProductId,
      productName: projectList?.find((el) => el?.value === selectedProductId)
        ?.label,
      groupId: selectedGroupId,
      groupName: roleList?.find((el) => el?.id === selectedGroupId)?.roleName,
    };

    setList(updatedList);
    setEditIndex(null);
    form.resetFields();
  };

  const handleCancelEdit = () => {
    setEditIndex(null);
    form.resetFields();
  };

  const handleRemove = (index: number) => {
    setList((prev) => prev.filter((_, idx) => idx !== index));
  };

  return (
    <Form
      component={false}
      className="custom-form"
      layout="vertical"
      requiredMark="optional"
      size="large"
      form={form}
      onFinish={handleAdd}
    >
      <div className="grid grid-cols-3 items-center gap-4 mt-4">
        {list?.map((el, idx) => (
          <Fragment key={idx}>
            {editIndex === idx ? (
              <>
                {/* INLINE EDIT MODE */}
                <Form.Item
                  name="productId"
                  className="mb-0"
                  rules={[{ required: true, message: "Please select product" }]}
                >
                  <Select
                    placeholder="Select Product"
                    allowClear
                    showSearch
                    size="large"
                    filterOption={selectSearchOption}
                    options={projectList}
                    suffixIcon={<SelectDropdownLgIcon />}
                    loading={isProductLoading || isAssignLoading}
                    onChange={() => form.resetFields(["groupId"])}
                  />
                </Form.Item>

                <Form.Item
                  name="groupId"
                  className="mb-0"
                  rules={[{ required: true, message: "Please select group" }]}
                >
                  <Select
                    placeholder="Select User Group"
                    allowClear
                    showSearch
                    size="large"
                    filterOption={selectSearchOption}
                    options={roleList.map((role) => ({
                      label: role?.roleName,
                      value: role?.id,
                    }))}
                    suffixIcon={<SelectDropdownLgIcon />}
                    loading={isLoading}
                    disabled={!productId}
                  />
                </Form.Item>

                <div className="flex items-center gap-1">
                  <Button
                    type="text"
                    className="mini-button !text-green-700"
                    htmlType="button"
                    onClick={() => handleUpdate(idx)}
                  >
                    <DoubleCheckIcon />
                  </Button>

                  <Button
                    type="text"
                    className="mini-button"
                    htmlType="button"
                    onClick={handleCancelEdit}
                  >
                    <CloseIcon />
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* NORMAL VIEW MODE */}
                <div className="text-blue-600 text-sm font-medium">
                  {el.productName}
                </div>

                <div className="text-zinc-700 text-sm font-medium">
                  {el.groupName}
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    type="text"
                    // className="!p-0 !size-8 !text-[#1B4DFF]"
                    className="mini-button !text-[#1B4DFF]"
                    htmlType="button"
                    onClick={() => handleEdit(el, idx)}
                  >
                    <EditIcon />
                  </Button>

                  <Button
                    type="text"
                    className="mini-button !text-red-500"
                    htmlType="button"
                    onClick={() => handleRemove(idx)}
                  >
                    <DeleteIcon />
                  </Button>
                </div>
              </>
            )}
          </Fragment>
        ))}

        {/* ADD SECTION — HIDDEN WHEN EDITING */}
        {editIndex === null && (
          <>
            <Form.Item
              name="productId"
              className="mb-0"
              rules={[{ required: true, message: "Please select product" }]}
            >
              <Select
                placeholder="Select Product"
                allowClear
                showSearch
                size="large"
                filterOption={selectSearchOption}
                options={projectList}
                suffixIcon={<SelectDropdownLgIcon />}
                loading={isProductLoading || isAssignLoading}
                onChange={() => form.resetFields(["groupId"])}
              />
            </Form.Item>

            <Form.Item
              name="groupId"
              className="mb-0"
              rules={[{ required: true, message: "Please select group" }]}
            >
              <Select
                placeholder="Select User Group"
                allowClear
                showSearch
                size="large"
                filterOption={selectSearchOption}
                options={roleList.map((role) => ({
                  label: role?.roleName,
                  value: role?.id,
                }))}
                suffixIcon={<SelectDropdownLgIcon />}
                loading={isLoading}
                disabled={!productId}
              />
            </Form.Item>

            <div className="flex items-center mb-2.5">
              <Button
                type="text"
                className="mini-button"
                htmlType="button"
                onClick={() => form.submit()}
              >
                <AddIcon />
              </Button>
            </div>
          </>
        )}
      </div>
    </Form>
  );
}
